import { clearAllPageFilterState } from "../hooks/usePersistentPageState";

export function getAuthItem(key) {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(key) || localStorage.getItem(key);
}

export function doLogout() {
  try {
    const isSession = sessionStorage.getItem('token');
    if (isSession) {
      // Only clear the session layer (impersonation), preserving the underlying admin session
      ['token', 'accountId', 'isAdmin', 'userType', 'registrationComplete', 'forcePasswordChange'].forEach(key => {
        sessionStorage.removeItem(key);
      });
    } else {
      // Full logout - clear everything
      ['token', 'accountId', 'isAdmin', 'userType', 'registrationComplete', 'forcePasswordChange'].forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
    }
    // Clear persisted page filter/query state so the next user sees fresh defaults
    clearAllPageFilterState();
    // Notify other tabs/components
    window.dispatchEvent(new Event('authUpdate'));
  } catch (e) {
    console.warn('doLogout error', e);
  }
  // Redirect to login
  try {
    window.location.href = '/login';
  } catch (_) { }
}

export function setToken(token) {
  // Validate token - must be a non-empty string and not the string 'null'
  if (!token || token === 'null' || token === 'undefined' || typeof token !== 'string' || token.trim() === '') {
    console.warn('setToken: Invalid token provided, not setting', { token, type: typeof token });
    return;
  }
  localStorage.setItem('token', token);
  window.dispatchEvent(new Event('authUpdate'));
}

export function parseJwtExpiry(token) {
  if (!token) return null;
  try {
    const p = token.split('.')[1];
    if (!p) return null;
    const payload = JSON.parse(atob(p));
    if (payload && payload.exp) return payload.exp * 1000;
    return null;
  } catch (e) {
    return null;
  }
}

function decodeTokenPayload(token) {
  try {
    if (!token || token === 'null' || token === 'undefined') return null;
    const p = token.split('.')[1];
    if (!p) return null;
    return JSON.parse(atob(p.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (e) {
    return null;
  }
}

/** Impersonate is enabled only for Super Admins and Admin accounts, never from an impersonated session. */
export function canImpersonateFromCurrentSession(isSuperAdmin) {
  const payload = decodeTokenPayload(getAuthItem('token'));
  if (!payload || payload.impersonated) return false;
  return !!isSuperAdmin || String(payload.type || '').toLowerCase() === 'admin';
}

/** True inside an impersonated session when viewing an account other than the impersonated one. */
export function isImpersonatingOtherAccount(accountId) {
  const payload = decodeTokenPayload(getAuthItem('token'));
  if (!payload || !payload.impersonated) return false;
  return Number(payload.account_id) !== Number(accountId);
}

export async function impersonateAccount(api, targetAccountId) {
  if (!api) throw new Error('API client is required');

  // A new-window impersonation keeps its token in sessionStorage; clearing it below would send
  // this request with the original admin's localStorage token and start an impersonation chain.
  if (decodeTokenPayload(getAuthItem('token'))?.impersonated) {
    throw new Error('Impersonated users cannot impersonate others');
  }

  // Clear sessionStorage to ensure Same Window mode uses localStorage exclusively
  ['token', 'accountId', 'userType', 'isAdmin', 'registrationComplete'].forEach(k => sessionStorage.removeItem(k));

  // Do NOT store tokens or sensitive data in localStorage. Send current path to server so it can record redirect.
  const body = { target_account_id: targetAccountId, currentPath: (typeof window !== 'undefined' && window.location ? window.location.pathname + window.location.search : '/') };
  const res = await api.post('/auth/impersonate', body);
  if (!res || !res.data) throw new Error('No response from server');
  if (res.data.token) {
    // Mirror Login behavior: set token and normalized identity fields
    // Mirror Login behavior: set token and normalized identity fields
    setToken(res.data.token);
    const u = res.data.user || {};
    const userType = String(u.type || '').toLowerCase();
    if (u.account_id) localStorage.setItem('accountId', String(u.account_id));
    localStorage.setItem('userType', userType);
    localStorage.setItem('isAdmin', userType === 'admin' ? 'true' : 'false');
    localStorage.setItem('registrationComplete', u.registration ? 'true' : 'false');

    // Decide where to navigate for impersonated user (login-like rules but tailored)
    const registrationComplete = !!u.registration;

    if (userType === 'admin' || userType === 'officestaff' || userType === 'manager') {
      // Admin/officestaff/manager -> admin dashboard (my-account area)
      window.location.href = '/dashboard';
      return res.data;
    }

    if (userType === 'fieldstaff' || userType === 'outsourcestaff' || userType === 'outsourcedstaff') {
      // Temp staff & Outsource staff: if already registered, go to availability screen
      if (registrationComplete) {
        window.location.href = '/availability';
      } else {
        // Otherwise, send to registration flow
        const acct = u.account_id;
        const acctParam = acct ? `?accountId=${acct}` : '';
        window.location.href = `/register${acctParam}`;
      }
      return res.data;
    }

    if (userType === 'contact' || userType === 'client') {
      // Client contacts -> client dashboard
      window.location.href = '/client-dashboard';
      return res.data;
    }

    if (userType === 'candidate') {
      // Candidates: if already registered, open register page but indicate already-registered so UI can show a notice.
      // If not registered, send to registration flow for that account so their in-progress data loads.
      const acct = u.account_id;
      if (registrationComplete) {
        const acctParam = acct ? `?accountId=${acct}` : '';
        window.location.href = `/register${acctParam}${acctParam ? '&' : '?'}alreadyRegistered=1`;
      } else {
        const acctParam = acct ? `?accountId=${acct}` : '';
        window.location.href = `/register${acctParam}`;
      }
      return res.data;
    }

    // Fallback: if not registered, send to register; otherwise reload
    if (!registrationComplete) {
      const acctParam = u.account_id ? `?accountId=${u.account_id}` : '';
      window.location.href = `/register${acctParam}`;
    } else {
      window.location.reload();
    }

    return res.data;
  }
  throw new Error(res.data.message || 'Impersonation failed');
}

export async function exitImpersonation(api) {
  if (!api) throw new Error('API client is required');

  // NEW: Check session mode first and exit locally to prevent disturbing main window
  if (typeof window !== 'undefined' && sessionStorage.getItem('token')) {
    console.log('exitImpersonation: Session Mode detected. Clearing session locally only.');
    ['token', 'accountId', 'userType', 'isAdmin', 'registrationComplete'].forEach(k => sessionStorage.removeItem(k));
    window.dispatchEvent(new Event('authUpdate'));
    window.location.href = '/admin/accounts';
    return { success: true };
  }
  // Call server to exit impersonation and get restored admin token + redirect
  try {
    console.log('exitImpersonation: Starting exit impersonation process...');
    console.log('exitImpersonation: Current localStorage state', {
      token: localStorage.getItem('token') ? 'exists' : 'missing',
      accountId: localStorage.getItem('accountId'),
      isAdmin: localStorage.getItem('isAdmin')
    });

    console.log('exitImpersonation: Calling API to exit impersonation...');
    let res;
    try {
      res = await api.post('/auth/impersonate/exit', {});
    } catch (apiError) {
      console.error('exitImpersonation: API call failed', {
        error: apiError,
        message: apiError?.message,
        response: apiError?.response,
        responseData: apiError?.response?.data
      });
      throw apiError;
    }

    console.log('exitImpersonation: API response received', {
      hasRes: !!res,
      hasData: !!(res && res.data),
      responseKeys: res && res.data ? Object.keys(res.data) : [],
      fullResponse: res
    });

    // Validate response structure
    if (!res || !res.data) {
      console.error('exitImpersonation: Invalid response structure', res);
      throw new Error('Invalid response from server');
    }

    // Check if token exists and is valid
    const token = res.data.token;
    console.log('exitImpersonation: Token from response', {
      hasToken: !!token,
      tokenType: typeof token,
      tokenValue: token ? (token.substring(0, 50) + '...') : token,
      tokenLength: token ? token.length : 0
    });

    // Helper to validate a token string
    const isValidToken = (t) => {
      return !!t && typeof t === 'string' && t !== 'null' && t !== 'undefined' && t.trim() !== '';
    };

    if (!isValidToken(token)) {
      console.error('exitImpersonation: No valid token in response', {
        token,
        tokenType: typeof token,
        response: res.data
      });
      throw new Error('No valid token received from server');
    }

    // Logic split: Session Mode vs Local Mode
    if (sessionStorage.getItem('token')) {
      console.log('exitImpersonation: Session Mode. Clearing session, preserving local.');
      ['token', 'accountId', 'userType', 'isAdmin', 'registrationComplete'].forEach(k => sessionStorage.removeItem(k));
      window.dispatchEvent(new Event('authUpdate'));
      // Redirect immediately
      window.location.href = '/dashboard';
      return res.data;
    }

    // Local Mode: Restore token and identity to localStorage
    console.log('exitImpersonation: Restoring admin session to localStorage...');

    // Set identity fields FIRST so they are ready when authUpdate is dispatched
    const adminUser = res.data.user || {};
    if (adminUser.account_id) {
      localStorage.setItem('accountId', String(adminUser.account_id));
    }

    // Decode token to verify admin status from token payload (source of truth)
    let tokenPayload = null;
    try {
      const payload = token.split('.')[1];
      if (payload) {
        tokenPayload = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      }
    } catch (e) {
      console.warn('exitImpersonation: Could not decode token payload', e);
    }

    const isAdminFromToken = tokenPayload?.is_admin === true || tokenPayload?.type === 'admin';
    const isAdminFromResponse = adminUser.is_admin === true;
    const shouldBeAdmin = isAdminFromToken || isAdminFromResponse;

    localStorage.setItem('userType', 'admin');
    localStorage.setItem('isAdmin', shouldBeAdmin ? 'true' : 'false');
    localStorage.setItem('registrationComplete', 'true');
    localStorage.setItem('token', token); // Set token last before dispatching

    // Verify token was set correctly
    const verifyToken = localStorage.getItem('token');
    if (!verifyToken || verifyToken === 'null') {
      console.error('exitImpersonation: Token was not set correctly');
      throw new Error('Failed to restore admin token');
    }

    console.log('exitImpersonation: Local state restored', {
      accountId: localStorage.getItem('accountId'),
      isAdmin: localStorage.getItem('isAdmin'),
      hasToken: !!verifyToken
    });

    // Notify hooks once everything is ready
    window.dispatchEvent(new Event('authUpdate'));

    // Refresh canonical profile info from server (best-effort, non-blocking)
    // IMPORTANT: Don't overwrite isAdmin if we already set it to 'true' for admin
    try {
      const profileRes = await api.get('/api/my-account/account/me');
      if (profileRes && profileRes.data && profileRes.data.data) {
        const d = profileRes.data.data;
        if (d.account_id) localStorage.setItem('accountId', String(d.account_id));
        localStorage.setItem('userType', String(d.type || 'admin').toLowerCase());
        // Preserve isAdmin='true' if already set (for admin accounts)
        const currentIsAdmin = localStorage.getItem('isAdmin');
        if (currentIsAdmin !== 'true') {
          localStorage.setItem('isAdmin', (d.type === 'admin' || d.is_admin) ? 'true' : 'false');
        }
        localStorage.setItem('registrationComplete', d.registration ? 'true' : 'false');
        window.dispatchEvent(new Event('authUpdate'));
      }
    } catch (e) {
      // Best-effort only; continue with values already set above
      console.warn('exitImpersonation: profile fetch failed (non-fatal)', e?.message || e);
    }

    // Same-window exit always returns the admin to the Accounts list,
    // regardless of which page impersonation was started from or what the
    // impersonated session's current route is (New-window exit is handled
    // separately above via the sessionStorage branch and is unaffected by
    // this). Wait briefly below to ensure token + cookies are settled so
    // route guards won't trigger a race.
    const redirectTo = '/admin/accounts';

    // Final verification before redirect
    const finalCheck = {
      token: localStorage.getItem('token') ? 'exists' : 'missing',
      accountId: localStorage.getItem('accountId'),
      isAdmin: localStorage.getItem('isAdmin'),
      userType: localStorage.getItem('userType')
    };
    console.log('exitImpersonation: Final check before redirect', finalCheck);

    if (finalCheck.isAdmin !== 'true') {
      console.error('exitImpersonation: WARNING - isAdmin is not "true" before redirect!', finalCheck);
      // Force set it one more time from token payload
      if (tokenPayload && (tokenPayload.is_admin === true || tokenPayload.type === 'admin')) {
        localStorage.setItem('isAdmin', 'true');
        console.log('exitImpersonation: Force-set isAdmin to true from token payload');
      }
    }

    try {
      // Allow event loop to settle and API interceptors to pick up token
      // Increased delay to ensure localStorage and events propagate
      await new Promise((r) => setTimeout(r, 300));
      console.log('exitImpersonation: Redirecting to', redirectTo);
      window.location.replace(redirectTo);
    } catch (_) {
      try { window.location.href = redirectTo; } catch (_) { window.location.reload(); }
    }
    return res.data;
  } catch (e) {
    console.error('exitImpersonation: server exit failed', e?.message || e, e);
    // Don't clear token on error - keep the current session
    // Just redirect to admin accounts and let the app handle auth
    try { window.location.href = '/admin/accounts'; } catch (_) {
      try { window.location.reload(); } catch (_) { }
    }
    return { restored: false, error: e?.message || 'Unknown error' };
  }
}

export async function generateImpersonationLink(api, targetAccountId) {
  if (!api) throw new Error('API client is required');
  const res = await api.post('/auth/impersonate/link', { target_account_id: targetAccountId });
  if (!res.data || !res.data.link) throw new Error('Failed to generate link');
  return res.data.link;
}

export async function verifyImpersonationTicket(api, ticket) {
  if (!api) throw new Error('API client is required');
  const res = await api.post('/auth/impersonate/verify-link', { ticket });
  if (res.data && res.data.token) {
    // USE SESSION STORAGE
    sessionStorage.setItem('token', res.data.token);

    const u = res.data.user || {};
    const userType = String(u.type || '').toLowerCase();

    if (u.account_id) sessionStorage.setItem('accountId', String(u.account_id));
    sessionStorage.setItem('userType', userType);
    sessionStorage.setItem('isAdmin', userType === 'admin' ? 'true' : 'false');
    sessionStorage.setItem('registrationComplete', u.registration ? 'true' : 'false');

    window.dispatchEvent(new Event('authUpdate'));

    let redirectTo = '/dashboard';
    if (userType === 'admin' || userType === 'officestaff' || userType === 'manager') {
      redirectTo = '/dashboard';
    } else if (userType === 'fieldstaff' || userType === 'outsourcestaff' || userType === 'outsourcedstaff') {
      redirectTo = '/availability';
    } else if (userType === 'contact' || userType === 'client') {
      redirectTo = '/client-dashboard';
    } else if (userType === 'candidate') {
      const acct = u.account_id;
      const registrationComplete = !!u.registration;
      if (registrationComplete) redirectTo = `/register?accountId=${acct}&alreadyRegistered=1`;
      else redirectTo = `/register?accountId=${acct}`;
    }

    try {
      window.location.href = redirectTo;
    } catch (_) {
      window.location.reload();
    }
    return res.data;
  }
  throw new Error(res.data.message || 'Verification failed');
}
